import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { api, fmtCurrency, fmtDateShort } from "@/lib/api";
import { Receipt, Search } from "lucide-react";

export function GlobalSearch({ open, onOpenChange }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
      return;
    }
  }, [open]);

  const runSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 1) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/search", { params: { q, limit: 12 } });
      setResults(data);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 180);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, runSearch]);

  const select = (id) => {
    onOpenChange(false);
    nav(`/orders/${id}`);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search orders, customers, PO, tracking, email, SKU…"
        value={query}
        onValueChange={setQuery}
        data-testid="global-search-input"
      />
      <CommandList>
        {!query && (
          <div className="px-6 py-10 text-center text-sm text-muted-foreground">
            <Search className="w-6 h-6 mx-auto mb-3 opacity-50" />
            <div>Type to search across every order field.</div>
            <div className="mt-1 text-xs">Try a PO number, customer name, tracking ID, or SKU.</div>
          </div>
        )}
        {query && results.length === 0 && !loading && (
          <CommandEmpty>No orders found for &ldquo;{query}&rdquo;.</CommandEmpty>
        )}
        {results.length > 0 && (
          <CommandGroup heading={`${results.length} order${results.length === 1 ? "" : "s"}`}>
            {results.map((r) => (
              <CommandItem
                key={r.id}
                value={`${r.number} ${r.customer} ${r.match_value || ""} ${r.id}`}
                onSelect={() => select(r.id)}
                data-testid={`search-result-${r.number}`}
                className="cursor-pointer"
              >
                <div className="flex items-center gap-3 w-full">
                  <Receipt className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-medium">{r.number}</span>
                      {r.store && (
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{r.store}</span>
                      )}
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground capitalize">{(r.status || "").replace(/_/g, " ")}</span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {r.customer}{r.company ? ` · ${r.company}` : ""}
                      {r.match_label && r.match_value && !["Order", "Customer"].includes(r.match_label) && (
                        <span className="ml-2 text-foreground/70">
                          · {r.match_label}: <span className="font-mono">{r.match_value}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm num font-semibold">{fmtCurrency(r.total || 0)}</div>
                    <div className="text-[11px] text-muted-foreground">{fmtDateShort(r.created_at)}</div>
                  </div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}

export function GlobalSearchTrigger({ onOpen }) {
  const isMac = typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  return (
    <button
      onClick={onOpen}
      data-testid="global-search-trigger"
      className="hidden sm:flex items-center gap-2 px-3 h-9 rounded-md border border-border bg-white hover:bg-secondary text-sm text-muted-foreground transition-colors min-w-[260px]"
    >
      <Search className="w-4 h-4" />
      <span>Search orders, customers, PO…</span>
      <kbd className="ml-auto text-[10px] font-mono bg-secondary px-1.5 py-0.5 rounded border border-border">
        {isMac ? "⌘K" : "Ctrl K"}
      </kbd>
    </button>
  );
}
